// Activity-feed data layer for the Console.
//
// Reads the keyset-paginated organizer activity log from
//   POST /api/v1/event/:eventId/activity/feed
// (backend complete + in production). The same rows arrive live as
// `activity.created` broadcasts over the realtime socket — the
// use-activity-feed hook merges the two (see src/lib/use-activity-feed.ts).
//
// The `activity_events` table is append-only: each row is a (verb, category,
// importance) tuple with denormalized actor/target snapshots so the feed can
// render without joins, plus a `summary` line and a freetext `metadata` blob.

import { infiniteQueryOptions } from "@tanstack/react-query"
import { api } from "./api"

export type ActivityImportance = "info" | "notable" | "critical"
export type ActivityActorType = "guest" | "organizer" | "contractor" | "system"
export type ActivityCategory =
  | "rsvp"
  | "chat"
  | "attendance"
  | "forms"
  | "travel"
  | "guest"
  | "system"

// Mirrors the backend `activity_events` row. `category`/`actorType` are kept as
// plain strings (not the unions) so a server-side enum addition never trips the
// iOS-style strict-decoder field-drop on the web — unknown values fall through
// to the `system` fallback in the UI meta lookup.
export type ActivityEvent = {
  id: string
  organizationId: string
  eventId: string
  verb: string
  category: string
  importance: ActivityImportance
  actorType: ActivityActorType
  actorId: string | null
  actorNameSnapshot: string | null
  targetType: string | null
  targetId: string | null
  targetLabelSnapshot: string | null
  summary: string
  metadata: string | null
  occurredAt: string
  createdAt?: string
}

export type ActivityFilters = {
  categories?: string[]
  actorTypes?: string[]
  importance?: string[]
}

// Keyset cursor — (occurredAt, id) is the server's stable reverse-chron order.
export type ActivityCursor = { occurredAt: string; id: string }

export type ActivityFeedPage = {
  items: ActivityEvent[]
  nextCursor: ActivityCursor | null
  hasMore: boolean
}

export const ACTIVITY_PAGE_SIZE = 50

// Every known category, in display order — drives the filter chips and the
// "all selected" tri-state.
export const ACTIVITY_CATEGORIES: ActivityCategory[] = [
  "rsvp",
  "chat",
  "attendance",
  "forms",
  "guest",
  "travel",
  "system",
]

export const ACTIVITY_IMPORTANCE: ActivityImportance[] = [
  "critical",
  "notable",
  "info",
]

// Reverse-chronological comparator: newest occurredAt first, id as the stable
// tiebreaker (mirrors the server's keyset order). Used to re-sort the merged
// live+history list.
export function byOccurredDesc(a: ActivityEvent, b: ActivityEvent): number {
  if (a.occurredAt === b.occurredAt) return a.id < b.id ? 1 : a.id > b.id ? -1 : 0
  return a.occurredAt < b.occurredAt ? 1 : -1
}

// Client-side predicate for the LIVE buffer — broadcasts arrive unfiltered, so
// we apply the active filter set before prepending (the history query applies
// the same filters server-side).
export function activityMatchesFilters(
  row: ActivityEvent,
  f: ActivityFilters,
): boolean {
  if (f.categories?.length && !f.categories.includes(row.category)) return false
  if (f.actorTypes?.length && !f.actorTypes.includes(row.actorType)) return false
  if (f.importance?.length && !f.importance.includes(row.importance)) return false
  return true
}

// Keyset-paginated infinite query for the activity history. `enabled` is gated
// on eventId by the caller. Each page POSTs the cursor of the previous page.
export function activityFeedQueryOptions(
  eventId: string | undefined,
  filters: ActivityFilters,
) {
  return infiniteQueryOptions({
    queryKey: ["activity-feed", eventId, filters] as const,
    initialPageParam: null as ActivityCursor | null,
    queryFn: ({ pageParam }) =>
      api<ActivityFeedPage>(`/api/v1/event/${eventId}/activity/feed`, {
        method: "POST",
        body: {
          eventId,
          categories: filters.categories,
          actorTypes: filters.actorTypes,
          importance: filters.importance,
          cursor: pageParam ?? undefined,
          limit: ACTIVITY_PAGE_SIZE,
        },
      }),
    getNextPageParam: (last: ActivityFeedPage) => last.nextCursor ?? undefined,
  })
}
